import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { BACKEND } from "../../../config/configEnvs";
import ModalInDrawer from "../../ui/ModalInDrawer";
import LoadingScreen from "../../ui/temp-screens/LoadingScreen";
import DetailsButton from "../../ui/components/DetailsButton";
import { FaEyeSlash, FaEdit, FaTimes, FaSave } from "react-icons/fa";
import { taskStatusColorMap } from "../../../utils/GetTextColor";
import { useSelector } from "react-redux";
import MainSaveButtonFullWidth from "../../ui/components/MainSaveButtonFullWidth";

const getNowDateTimeLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = now.getFullYear();
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const min = pad(now.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("es-PE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const OpenMeets = ({ open, onClose, serviceId }) => {
  const [meets, setMeets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingMeetLink, setUpdatingMeetLink] = useState(false);
  const user = useSelector((state) => state.auth.user);

  const [objectives, setObjectives] = useState([]);
  const [newMeet, setNewMeet] = useState({
    objective: "",
    name: "",
    deliverDate: getNowDateTimeLocal(),
    linkMeet: "",
  });

  // edición inline SOLO del linkMeet
  const [editingMeetId, setEditingMeetId] = useState(null);
  const [editingLinkMeet, setEditingLinkMeet] = useState("");

  const getMeets = async () => {
    if (!serviceId) return;
    try {
      setLoading(true);
      const url = `${BACKEND}/api/tareasProyectos?where[serviceId][equals]=${serviceId}&where[isMeeting][equals]=true&limit=0&where[status][equals]=Finalizado`;
      const response = await axios.get(url);

      console.log(response.data.totalDocs)
      setMeets(response.data.docs || []);
    } catch (error) {
      toast.error("Error al obtener las reuniones");
    } finally {
      setLoading(false);
    }
  };

  const getObjectives = async () => {
    if (!serviceId) return;
    try {
      const response = await axios.get(
        `${BACKEND}/api/objetivosProyectos?where[phase.service][equals]=${serviceId}&limit=0`
      );
      setObjectives(response.data.docs || []);
    } catch (e) {
      toast.error("Error al obtener objetivos");
    }
  };

  useEffect(() => {
    if (open) {
      getMeets();
      getObjectives();
      setNewMeet((prev) => ({
        ...prev,
        deliverDate: prev.deliverDate || getNowDateTimeLocal(),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serviceId]);

  const rows = useMemo(() => {
    return (meets || []).map((t) => {
      return {
        id: t.id,
        name: t?.task?.name || t?.name || "Sin título",
        status: t?.status || "-",
        responsible: t?.responsible?.name || t?.responsible?.email || "-",
        startDate: t?.startDate,
        linkMeet: t?.linkMeet || "",
        deliverDate: t?.deliverDate || "",
        delivered: t?.delivered || false,
      }
    });
  }, [meets]);

  const startEditMeetLink = (row) => {
    // Si ya hay linkMeet, ocultamos la edición (solo se puede registrar una vez)
    if (row?.linkMeet && String(row.linkMeet).trim() !== "") return;
    setEditingMeetId(row.id);
    setEditingLinkMeet(row.linkMeet || "");
  };

  const cancelEditMeetLink = () => {
    setEditingMeetId(null);
    setEditingLinkMeet("");
  };

  const saveMeetLink = async () => {
    try {
      if (!editingMeetId) return;
      setUpdatingMeetLink(true);
      await axios.put(`${BACKEND}/api/tareasProyectos/${editingMeetId}`, {
        linkMeet: (editingLinkMeet || "").trim(),
      });
      toast.success("Link Meet actualizado");
      cancelEditMeetLink();
      await getMeets();
    } catch (e) {
      toast.error("Error al actualizar el link Meet");
    } finally {
      setUpdatingMeetLink(false);
    }
  };

  const resetNewMeet = () => {
    setNewMeet({
      objective: "",
      name: "",
      deliverDate: getNowDateTimeLocal(),
      linkMeet: "",
    });
  };

  const createMeetTask = async () => {
    try {
      if (!serviceId) return;
      if (!newMeet.objective) {
        toast.warning("Selecciona un objetivo");
        return;
      }
      if (!newMeet.name || newMeet.name.trim().length < 2) {
        toast.warning("Escribe el nombre de la reunión");
        return;
      }
      if (!newMeet.deliverDate) {
        toast.warning("Selecciona fecha y hora");
        return;
      }

      setUpdating(true);

      const responseTask = await axios.post(`${BACKEND}/api/tareas`, {
        name: newMeet.name.trim(),
        defaultObjective: null,
        defaultService: null,
        defaultResponsible: null,
        isProofNeeded: false,
        isVisit: false,
        isMeeting: true,
      });

      const createdLinkMeet = (newMeet.linkMeet || "").trim();
      const responseTaskProject = await axios.post(`${BACKEND}/api/tareasProyectos`, {
        task: responseTask.data.doc.id,
        objective: newMeet.objective,
        responsible: user?.id,
        serviceId: serviceId,
        isProofNeeded: false,
        isVisit: false,
        isMeeting: true,
        // para que aparezca en tu lista actual (filtrada por Finalizado)
        status: "Finalizado",
        startDate: newMeet.deliverDate,
        deliverDate: newMeet.deliverDate,
        linkMeet: createdLinkMeet,
        links: [],
        audios: [],
      });

      toast.success("Reunión creada");
      resetNewMeet();
      await getMeets();

      // una vez creada, permitir editar SOLO el link del meet
      const createdId = responseTaskProject?.data?.doc?.id || responseTaskProject?.data?.id;
      if (createdId) {
        setEditingMeetId(createdId);
        setEditingLinkMeet(createdLinkMeet);
      }
    } catch (e) {
      toast.error("Error al crear la reunión");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <ModalInDrawer
      activeModal={open}
      onClose={onClose}
      title="Reuniones cliente"
      centered
      className="max-w-6xl"
      scrollContent={false}
    >
      {loading ? (
        <div className="h-[15rem]">
          <LoadingScreen text="Cargando reuniones..." />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">
              Total: {rows.length}
            </span>
            <button
              type="button"
              onClick={getMeets}
              className="text-xs font-semibold px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
            >
              Recargar
            </button>
          </div>

          <div className="w-full overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Reunión</th>
                  <th className="px-3 py-2">Responsable</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2 text-center">Meet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-4 text-gray-500" colSpan={5}>
                      No hay reuniones registradas para este servicio.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDateTime(r.deliverDate)}
                      </td>
                      <td className="px-3 py-2">{r.name}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.responsible}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={taskStatusColorMap[r.status]}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          {editingMeetId === r.id ? (
                            <div className="flex items-center gap-2 w-full justify-center">
                              <input
                                type="text"
                                value={editingLinkMeet}
                                onChange={(e) => setEditingLinkMeet(e.target.value)}
                                placeholder="https://meet.google.com/..."
                                className="text-xs w-[260px] rounded-md bg-gray-50 border border-gray-300 px-2 py-1 outline-none"
                              />

                              <button
                                type="button"
                                onClick={saveMeetLink}
                                disabled={updatingMeetLink}
                                className="p-2 rounded-md bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                                title="Guardar"
                              >
                                <FaSave className="text-[12px]" />
                              </button>

                              <button
                                type="button"
                                onClick={cancelEditMeetLink}
                                disabled={updatingMeetLink}
                                className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                title="Cancelar"
                              >
                                <FaTimes className="text-[12px]" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              {r.linkMeet ? (
                                <DetailsButton
                                  action={() => {window.open(r.linkMeet, "_blank")}}
                                  content={r.linkMeet ? r.linkMeet : "No hay link de reunión"}  
                                  type="externalLink"
                                />
                              ) : (
                                <FaEyeSlash className="text-gray-500"/>
                              )}

                              {!r.linkMeet && (
                                <button
                                  type="button"
                                  onClick={() => startEditMeetLink(r)}
                                  className="p-2 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                                  title="Registrar link Meet"
                                >
                                  <FaEdit className="text-[12px]" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Crear nuevo (visual como OpenClientReviewsRequests) */}
          <div className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              Crear nueva reunión
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex flex-col w-full sm:w-[260px]">
                  <label className="text-xs text-gray-600 mb-1">Objetivo</label>
                  <select
                    value={newMeet.objective}
                    onChange={(e) =>
                      setNewMeet((p) => ({ ...p, objective: e.target.value }))
                    }
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none"
                  >
                    <option value="">Seleccionar</option>
                    {objectives.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o?.objective?.name || "Objetivo"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col w-full">
                  <label className="text-xs text-gray-600 mb-1">
                    Nombre de la reunión
                  </label>
                  <input
                    type="text"
                    value={newMeet.name}
                    onChange={(e) =>
                      setNewMeet((p) => ({ ...p, name: e.target.value }))
                    }
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none"
                    placeholder="Ej: Reunión de revisión 1"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex flex-col w-full sm:w-[260px]">
                  <label className="text-xs text-gray-600 mb-1">
                    Fecha y hora
                  </label>
                  <input
                    type="datetime-local"
                    value={newMeet.deliverDate}
                    onChange={(e) =>
                      setNewMeet((p) => ({ ...p, deliverDate: e.target.value }))
                    }
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label className="text-xs text-gray-600 mb-1">Link Meet</label>
                  <input
                    type="text"
                    value={newMeet.linkMeet}
                    onChange={(e) =>
                      setNewMeet((p) => ({ ...p, linkMeet: e.target.value }))
                    }
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none"
                    placeholder="https://meet.google.com/..."
                  />
                </div>
              </div>

              <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                <span className="font-semibold">Nota:</span> si registras la reunión desde este apartado, se guardará automáticamente con estado <span className="font-semibold">Finalizado</span>.
              </div>

              <MainSaveButtonFullWidth
                loading={updating}
                text="Registrar reunión"
                onClick={createMeetTask}
                disabled={
                  updating ||
                  !newMeet.objective ||
                  !newMeet.name.trim() ||
                  !newMeet.deliverDate
                }
                marginTop="0"
              />
            </div>
          </div>
        </div>
      )}
    </ModalInDrawer>
  );
};

export default OpenMeets;